import dayjs from "dayjs";
import {
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useImmerReducer } from "use-immer";
import { useAuth } from "../../hooks/useAuth";
import { PortfolioPermissions } from "../../api/compute/PortfolioPermissions";
import { useFeedbackSystem } from "../../hooks/useFeedbackSystem"; 

type FormFields = {
  email: string;
  firstName: string;
  lastName: string;
  accountType: string;
  profile: string;
  username: string;
  portfolioNumber: string;
  country: string;
  firm: string;
  owner: string;
  contractType: string;
};

type ActionType = {
  type: string;
};

type InitReducerState = {
  accountType: { isValid: boolean; message: string };
  owner: { isValid: boolean; message: string };
  firstName: { isValid: boolean; message: string };
  lastName: { isValid: boolean; message: string };
  email: { isValid: boolean; message: string };
  country: { isValid: boolean; message: string };
  firm: { isValid: boolean; message: string };
  profile: { isValid: boolean; message: string };
  userName: { isValid: boolean; message: string };
  contractType: { isValid: boolean; message: string };
  portfolios: { isValid: boolean; message: string };
};

export const useValidation = (user: FormFields) => {
  const VALIDATE_TYPE = "VALIDATE_TYPE";
  const VALIDATE_OWNER = "VALIDATE_OWNER";
  const VALIDATE_FIRSTNAME = "VALIDATE_FIRSTNAME";
  const VALIDATE_LASTNAME = "VALIDATE_LASTNAME";
  const VALIDATE_EMAIL = "VALIDATE_EMAIL";
  const VALIDATE_COUNTRY = "VALIDATE_COUNTRY";
  const VALIDATE_FIRM = "VALIDATE_FIRM";
  const VALIDATE_PROFILE = "VALIDATE_PROFILE";
  const VALIDATE_USERNAME = "VALIDATE_USERNAME";
  const VALIDATE_CONTRACT_TYPE = "VALIDATE_CONTRACT_TYPE";
  const VALIDATE_PORTFOLIOS = "VALIDATE_PORTFOLIOS";

  const initialState = {
    accountType: { isValid: false, message: "" },
    owner: { isValid: false, message: "" },
    firstName: { isValid: false, message: "" },
    lastName: { isValid: false, message: "" },
    email: { isValid: false, message: "" },
    country: { isValid: false, message: "" },
    firm: { isValid: false, message: "" },
    profile: { isValid: false, message: "" },
    userName: { isValid: false, message: "" },
    contractType: { isValid: false, message: "" },
    portfolios: { isValid: false, message: "" },
  };

  const reducer = (draft: InitReducerState, action: ActionType) => {
    switch (action.type) {
      case VALIDATE_TYPE:
        if (
          user.accountType.length &&
          user.accountType !== "Choose an option" &&
          user.accountType !== "unset"
        ) {
          draft.accountType = {
            isValid: true,
            message: "",
          };
        } else {
          draft.accountType = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      case VALIDATE_OWNER:
        if (
          user.owner &&
          user.owner !== "Choose an option" &&
          user.owner !== "unset"
        ) {
          draft.owner = {
            isValid: true,
            message: "",
          };
        } else {
          draft.owner = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      case VALIDATE_FIRSTNAME:
        if (user.firstName.length) {
          draft.firstName = {
            isValid: true,
            message: "",
          };
        } else {
          draft.firstName = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      case VALIDATE_LASTNAME:
        if (user.lastName.length) {
          draft.lastName = {
            isValid: true,
            message: "",
          };
        } else {
          draft.lastName = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      case VALIDATE_EMAIL:
        if (user.email.length) {
          draft.email = {
            isValid: true,
            message: "",
          };
        } else {
          draft.email = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      case VALIDATE_COUNTRY:
        if (
          user?.country &&
          user.country.length &&
          user.country !== "Choose a country" &&
          user.country !== "unset"
        ) {
          draft.country = {
            isValid: true,
            message: "",
          };
        } else {
          draft.country = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      case VALIDATE_FIRM:
        if (user.firm && user.firm.length) {
          draft.firm = {
            isValid: true,
            message: "",
          };
        } else {
          draft.firm = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      case VALIDATE_PROFILE:
        if (
          user.profile &&
          user.profile.length &&
          user.profile !== "Choose an option" &&
          user.profile !== "unset"
        ) {
          draft.profile = {
            isValid: true,
            message: "",
          };
        } else {
          draft.profile = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      case VALIDATE_USERNAME:
        if (user.username.length) {
          draft.userName = {
            isValid: true,
            message: "",
          };
        } else {
          draft.userName = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      case VALIDATE_CONTRACT_TYPE:
        if (
          user.contractType.length &&
          user.contractType !== "Choose an option" &&
          user.contractType !== "unset"
        ) {
          draft.contractType = {
            isValid: true,
            message: "",
          };
        } else {
          draft.contractType = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      case VALIDATE_PORTFOLIOS:
        if (
          JSON.stringify(user.portfolioNumber).length &&
          !isNaN(parseInt(user.portfolioNumber))
        ) {
          draft.portfolios = {
            isValid: true,
            message: "",
          };
        } else {
          draft.portfolios = {
            isValid: false,
            message: "This field is required",
          };
        }
        break;

      default:
        return draft;
    }
  };

  const [state, dispatch] = useImmerReducer(reducer, initialState);

  useEffect(() => {
    dispatch({ type: VALIDATE_EMAIL });
  }, [dispatch, user.email]);

  useEffect(() => {
    dispatch({ type: VALIDATE_TYPE });
  }, [dispatch, user.accountType]);

  useEffect(() => {
    dispatch({ type: VALIDATE_OWNER });
  }, [dispatch, user.owner]);

  useEffect(() => {
    dispatch({ type: VALIDATE_FIRSTNAME });
  }, [dispatch, user.firstName]);

  useEffect(() => {
    dispatch({ type: VALIDATE_LASTNAME });
  }, [dispatch, user.lastName]);

  useEffect(() => {
    dispatch({ type: VALIDATE_COUNTRY });
  }, [dispatch, user.country]);

  useEffect(() => {
    dispatch({ type: VALIDATE_FIRM });
  }, [dispatch, user.firm]);

  useEffect(() => {
    dispatch({ type: VALIDATE_PROFILE });
  }, [dispatch, user.profile]);

  useEffect(() => {
    dispatch({ type: VALIDATE_USERNAME });
  }, [dispatch, user.username]);

  useEffect(() => {
    dispatch({ type: VALIDATE_CONTRACT_TYPE });
  }, [dispatch, user.contractType]);

  useEffect(() => {
    dispatch({ type: VALIDATE_PORTFOLIOS });
  }, [dispatch, user.portfolioNumber]);

  const fields = [
    state.firstName,
    state.lastName,
    state.accountType,
    state.owner,
    state.email,
    state.country,
    state.firm,
    state.profile,
    state.userName,
    state.contractType,
    state.portfolios,
  ];

  const invalidFields = fields.find(
    (item: { isValid: boolean; message: string }) => item.isValid === false
  );

  const isFormValid = invalidFields ? false : true;

  return { state, isFormValid };
};

type PortfolioUser = {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  userName: string;
  role: "STANDARD";
  profile: string;
  type: string;
  firm: string;
  active: boolean;
  country: string;
  products: string;
  contractType: string;
  productMax: number;
  billingStart: string;
  conversionDate: string;
  owner: string;
  password: string;
  stripeId: null;
};

export const userSchema: PortfolioUser = {
  id: undefined,
  firstName: "",
  lastName: "",
  email: "",
  userName: "",
  role: "STANDARD",
  profile: "unset",
  type: "unset",
  firm: "",
  active: false,
  country: "unset",
  products: "all",
  contractType: "unset",
  productMax: 100,
  billingStart: "",
  conversionDate: "",
  owner: "unset",
  password: "",
  stripeId: null,
};

export const usePortfolioUser = (
  operationType: "CONVERT" | "UPDATE" | "CREATE",
  existingUser?: PortfolioUser
) => {
  type UserAction =
    | { type: "FIRST_NAME"; payload: string }
    | { type: "LAST_NAME"; payload: string }
    | { type: "EMAIL"; payload: string }
    | { type: "USER_NAME"; payload: string }
    | { type: "PROFILE"; payload: string }
    | { type: "TYPE"; payload: string }
    | { type: "FIRM"; payload: string }
    | { type: "ACTIVE"; payload: boolean }
    | { type: "COUNTRY"; payload: string }
    | { type: "CONTRACT_TYPE"; payload: string }
    | { type: "PRODUCT_MAX"; payload: number }
    | { type: "BILLING_START"; payload: string }
    | { type: "CONVERSION_DATE"; payload: string }
    | { type: "OWNER"; payload: string };

  const updateUserReducer: Reducer<PortfolioUser, UserAction> = (
    state,
    action
  ) => {
    switch (action.type) {
      case "FIRST_NAME":
        return {
          ...state,
          firstName: action.payload.trim(),
        };
      case "LAST_NAME": {
        return { ...state, lastName: action.payload.trim() };
      }
      case "EMAIL": {
        // When email is changed use it to prefill the username field
        return {
          ...state,
          email: action.payload.trim(),
          userName: action.payload.trim(),
        };
      }
      case "USER_NAME": {
        return {
          ...state,
          userName: action.payload.trim(),
        };
      }
      case "PROFILE": {
        return { ...state, profile: action.payload };
      }
      case "TYPE": {
        return { ...state, type: action.payload };
      }
      case "FIRM": {
        return { ...state, firm: action.payload.trim() };
      }
      case "ACTIVE": {
        return { ...state, active: action.payload };
      }
      case "COUNTRY": {
        return { ...state, country: action.payload.toUpperCase() };
      }
      case "CONTRACT_TYPE": {
        return { ...state, contractType: action.payload };
      }
      case "PRODUCT_MAX": {
        return { ...state, productMax: action.payload };
      }
      case "BILLING_START": {
        return {
          ...state,
          billingStart: dayjs(action.payload).format("YYYY-MM-DD"),
        };
      }
      case "CONVERSION_DATE": {
        return {
          ...state,
          conversionDate: dayjs(action.payload).format("YYYY-MM-DD"),
        };
      }
      case "OWNER": {
        return { ...state, owner: action.payload };
      }

      default:
        return state;
    }
  };

  const [theUser, updateUser] = useReducer(
    updateUserReducer,
    existingUser ?? userSchema
  );
  const { state, isFormValid } = useValidation({
    email: theUser.email,
    firstName: theUser.firstName,
    lastName: theUser.lastName,
    accountType: theUser.type,
    profile: theUser.profile,
    username: theUser.userName,
    portfolioNumber: JSON.stringify(theUser.productMax),
    country: theUser.country,
    firm: theUser.firm,
    owner: theUser.owner,
    contractType: theUser.contractType,
  });
  const auth = useAuth();
  const feedback = useFeedbackSystem();
  const [assignedPortfolios, setAssignedPortfolios] = useState<string[]>([]);

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );

  const permissions = useRef<any>({});
  const portfolioPermissionsAPI = useMemo(() => new PortfolioPermissions(), []);

  const loadUserPortfolios = useCallback(async () => {
    setStatus("loading");

    try {
      const portfoliosIds = await portfolioPermissionsAPI.getPermissions(
        auth.user.additionalCookies
      );

      permissions.current = portfoliosIds;

      // If is a conversion don't initialize the permission map with the id of the pendig user
      if (operationType !== "CONVERT" && theUser.id) {
        if (theUser.id in portfoliosIds) {
          setAssignedPortfolios(portfoliosIds[theUser.id]);
        } else {
          setAssignedPortfolios([]);
        }
      }

      setStatus("success");
    } catch (error) {
      feedback.show({
        type: "error",
        message: error as any,
      });
      setStatus("error");
    }
  }, [
    auth.user.additionalCookies,
    feedback,
    operationType,
    portfolioPermissionsAPI,
    theUser.id,
  ]);

  useEffect(() => {
    loadUserPortfolios();
  }, [loadUserPortfolios]);

  const changeAssignedPortfolios = useCallback(
    async (data) => {
      // Don't update the permissions with the id of the pending user
      if (operationType !== "CONVERT" && theUser.id) {
        permissions.current[theUser.id] = data;

        await portfolioPermissionsAPI.upload(
          permissions.current,
          auth.user.additionalCookies
        );
      }

      setAssignedPortfolios(data);
    },
    [
      auth.user.additionalCookies,
      operationType,
      portfolioPermissionsAPI,
      theUser.id,
    ]
  );

  const createUserService = useCallback(async () => {
    const userCopy = JSON.parse(JSON.stringify(theUser));

    if (operationType === "CONVERT") {
      // Remove id property if it exists (pending users have an id that is referred to the pending users table) but when we
      // create a user the id must always be null.
      userCopy["id"] = null;
    }

    // Set default password
    userCopy["password"] =
      userCopy["firstName"].trim().charAt(0).toUpperCase() +
      userCopy["firstName"].trim().slice(1) +
      "1";

    const createdUser = await portfolioPermissionsAPI.createNewUser(userCopy);
    const newUserId = createdUser?.["data"]?.["user"]?.["id"];

    if (newUserId) {
      permissions.current[newUserId] = assignedPortfolios;

      await portfolioPermissionsAPI.upload(
        permissions.current,
        auth.user.additionalCookies
      );
    }

    return createdUser["data"]["user"];
  }, [
    assignedPortfolios,
    auth.user.additionalCookies,
    operationType,
    portfolioPermissionsAPI,
    theUser,
  ]);

  const deleteUserService = useCallback(async () => {
    try {
      const response = await portfolioPermissionsAPI.deleteUser(theUser);

      if (theUser.id && theUser.id in permissions.current) {
        delete permissions.current[theUser?.id];

        await portfolioPermissionsAPI.upload(
          permissions.current,
          auth.user.additionalCookies
        );
      }

      return response;
    } catch (error) {
      console.log(error);
      return error;
    }
  }, [auth.user.additionalCookies, portfolioPermissionsAPI, theUser]);

  const updateUserService = useCallback(async () => {
    try {
      const userCopy = JSON.parse(JSON.stringify(theUser));

      // never override user password in an update operation

      if ("password" in userCopy) {
        delete userCopy["password"];
      }

      const response = await portfolioPermissionsAPI.updateUser(userCopy);
      return response["data"]["user"];
    } catch (error) {
      console.log(error);

      return error;
    }
  }, [portfolioPermissionsAPI, theUser]);

  const deleteUserFromPendingList = useCallback(async () => {
    const response = await portfolioPermissionsAPI.deleteUserFromPending(
      theUser.id
    );

    return response;
  }, [portfolioPermissionsAPI, theUser.id]);

  const convertUserService = useCallback(async () => {
    try {
      const user = await createUserService();
      deleteUserFromPendingList();
      return user;
    } catch (error) {
      console.log(error);
    }
  }, [createUserService, deleteUserFromPendingList]);

  return {
    user: new Proxy(theUser, {
      get: (obj, prop) => {
        if (prop === "billingStart_ToIso" || prop === "conversionDate_ToIso") {
          const [key] = prop.split("_");
          const date = obj[key];

          if (date != null && date !== "") {
            return dayjs(date, "YYYY-MM-DD").toISOString();
          }

          return undefined;
        } else {
          return obj[prop];
        }
      },
    }),
    updateUser,
    valid: isFormValid,
    errors: state,
    assignedPortfolios,
    changeAssignedPortfolios,
    status,
    createUserService,
    deleteUserService,
    updateUserService,
    convertUserService,
    deleteUserFromPendingList,
  };
};
